import moment, { type Moment } from 'moment';

import { createFile } from '#apps/client-web/features/file-storage/helpers';
import { type FileContent } from '#apps/client-web/features/file-storage/services/file-storage';

import type { Attachment } from '#packages/data-collection/models/common/attachment';
import { type AuditBackgroundForm } from '#packages/data-collection/models/audit-background-form';
import { isNil } from '#packages/data-collection/libs/lodash';

import { emptyFormData } from './constants';
import { type EditAuditFormBackgroundData } from './types';

const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

export const momentToFormDate = (momentObject: Moment): string =>
  momentObject.format(DATETIME_LOCAL_FORMAT);

export const formDateToMoment = (value: string): Moment =>
  moment(value, DATETIME_LOCAL_FORMAT, true);

export const fromFormData = (
  formData: EditAuditFormBackgroundData,
): Omit<AuditBackgroundForm, 'type'> => ({
  general: {
    ...formData.general,
    siteVisitStartDate: isNil(formData.general.siteVisitStartDate)
      ? undefined
      : formDateToMoment(formData.general.siteVisitStartDate),
    siteVisitEndDate: isNil(formData.general.siteVisitEndDate)
      ? undefined
      : formDateToMoment(formData.general.siteVisitEndDate),
    meters: (formData.general.meters ?? []).map(({ attachments, ...rest }) => ({
      ...rest,
      attachments: attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
        name,
        mimeType,
        isSelectedForReport,
      })),
    })),
    recentUpgrades: formData.general.recentUpgrades,
    plansForUpgrades: formData.general.plansForUpgrades,
  },
  envelope: {
    coverPhoto: formData.envelope.coverPhoto?.map(({ name, mimeType, isSelectedForReport }) => ({
      name,
      mimeType,
      isSelectedForReport,
    })),
    windowTypes: (formData.envelope.windowTypes ?? []).map(({ attachments, ...rest }) => ({
      ...rest,
      attachments: attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
        name,
        mimeType,
        isSelectedForReport,
      })),
    })),
    doorTypes: (formData.envelope.doorTypes ?? []).map(({ attachments, ...rest }) => ({
      ...rest,
      attachments: attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
        name,
        mimeType,
        isSelectedForReport,
      })),
    })),
    wallTypes: (formData.envelope.wallTypes ?? []).map(({ attachments, ...rest }) => ({
      ...rest,
      attachments: attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
        name,
        mimeType,
        isSelectedForReport,
      })),
    })),
    roofingTypes: (formData.envelope.roofingTypes ?? []).map(({ attachments, ...rest }) => ({
      ...rest,
      attachments: attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
        name,
        mimeType,
        isSelectedForReport,
      })),
    })),
  },
  mainBreaker: {
    ...formData.mainBreaker,
  },
  controls: {
    ...formData.controls,
    attachments: formData.controls.attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
      name,
      mimeType,
      isSelectedForReport,
    })),
  },
  spaceHeating: {
    ...formData.spaceHeating,
    systems: (formData.spaceHeating.systems ?? []).filter(
      (s): s is string => s != null,
    ),
  },
  spaceCooling: {
    ...formData.spaceCooling,
    systems: (formData.spaceCooling.systems ?? []).filter(
      (s): s is string => s != null,
    ),
  },
  ventilation: {
    ...formData.ventilation,
  },
  domesticColdWater: {
    ...formData.domesticColdWater,
  },
  domesticHotWater: {
    ...formData.domesticHotWater,
  },
  lighting: {
    ...formData.lighting,
  },
  buildingAndSystems: {
    attachments: formData.buildingAndSystems.attachments?.map(({ name, mimeType, isSelectedForReport }) => ({
      name,
      mimeType,
      isSelectedForReport,
    })),
  },
  other: {
    ...formData.other,
  },
});

export const toFormData = (
  data: AuditBackgroundForm | undefined,
  files?: (FileContent | undefined)[],
): EditAuditFormBackgroundData => {
  // Files are ordered to match the flat sequence:
  // meters[0].attachments, ..., controls.attachments,
  // envelope: coverPhoto, windowTypes[0].attachments, ..., doorTypes[0].attachments, ...
  // wallTypes[0].attachments, ..., roofingTypes[0].attachments, ...
  let fileIndex = 0;

  const rebuildAttachments = (attachments: Attachment[] | undefined) =>
    (attachments ?? []).map((attachment) => ({
      ...attachment,
      file: createFile({
        content: files![fileIndex++]!,
        name: attachment.name,
        mimeType: attachment.mimeType,
      }),
    }));

  const meters = (data?.general.meters ?? []).map((meter) => ({
    ...meter,
    attachments: rebuildAttachments(meter.attachments),
  }));

  const controlsAttachments = rebuildAttachments(data?.controls.attachments);
  const envelopeCoverPhoto = rebuildAttachments(data?.envelope.coverPhoto);

  const envelopeWindowTypes = (data?.envelope.windowTypes ?? []).map((entry) => ({
    ...entry,
    attachments: rebuildAttachments(entry.attachments),
  }));

  const envelopeDoorTypes = (data?.envelope.doorTypes ?? []).map((entry) => ({
    ...entry,
    attachments: rebuildAttachments(entry.attachments),
  }));

  const envelopeWallTypes = (data?.envelope.wallTypes ?? []).map((entry) => ({
    ...entry,
    attachments: rebuildAttachments(entry.attachments),
  }));

  const envelopeRoofingTypes = (data?.envelope.roofingTypes ?? []).map((entry) => ({
    ...entry,
    attachments: rebuildAttachments(entry.attachments),
  }));

  const buildingAndSystemsAttachments = rebuildAttachments(data?.buildingAndSystems.attachments);

  return {
    general: isNil(data?.general)
      ? {
          ...emptyFormData.general,
        }
      : {
          ...data.general,
          meters,
          siteVisitStartDate: isNil(data.general.siteVisitStartDate)
            ? undefined
            : momentToFormDate(data.general.siteVisitStartDate),
          siteVisitEndDate: isNil(data.general.siteVisitEndDate)
            ? undefined
            : momentToFormDate(data.general.siteVisitEndDate),
          recentUpgrades: data.general.recentUpgrades,
          plansForUpgrades: data.general.plansForUpgrades,
        },
    envelope: isNil(data?.envelope)
      ? {
          ...emptyFormData.envelope,
        }
      : {
          coverPhoto: envelopeCoverPhoto,
          windowTypes: envelopeWindowTypes,
          doorTypes: envelopeDoorTypes,
          wallTypes: envelopeWallTypes,
          roofingTypes: envelopeRoofingTypes,
        },
    mainBreaker: isNil(data?.mainBreaker)
      ? { ...emptyFormData.mainBreaker }
      : { ...data.mainBreaker },
    controls: isNil(data?.controls)
      ? { ...emptyFormData.controls }
      : { ...data.controls, attachments: controlsAttachments },
    spaceHeating: isNil(data?.spaceHeating)
      ? {
          ...emptyFormData.spaceHeating,
        }
      : { ...data.spaceHeating },
    spaceCooling: isNil(data?.spaceCooling)
      ? { ...emptyFormData.spaceCooling }
      : { ...data.spaceCooling },
    ventilation: isNil(data?.ventilation)
      ? {
          ...emptyFormData.ventilation,
        }
      : data.ventilation,
    domesticColdWater: isNil(data?.domesticColdWater)
      ? { ...emptyFormData.domesticColdWater }
      : { ...data.domesticColdWater },
    domesticHotWater: isNil(data?.domesticHotWater)
      ? { ...emptyFormData.domesticHotWater }
      : { ...data.domesticHotWater },
    lighting: isNil(data?.lighting)
      ? {
          ...emptyFormData.lighting,
        }
      : data.lighting,
    buildingAndSystems: isNil(data?.buildingAndSystems)
      ? { ...emptyFormData.buildingAndSystems }
      : { attachments: buildingAndSystemsAttachments },
    other: isNil(data?.other)
      ? {
          ...emptyFormData.other,
        }
      : data.other,
  };
};
